/** LocalWorkspaceRuntime projects the D4 local workspace wire face for UI consumers. */
import { createSnapshotStore } from "../contract/store.js";
/**
 * The EXISTING D1 authenticated pairing HTTP route (mounted by the device host
 * on the deployment web server and gated by the identity principal resolver).
 * The browser reads only the short-lived enrollment code; the device credential
 * is delivered to the device over its own authenticated outbound socket.
 */
const PAIRING_REQUEST_PATH = '/api/device.pairing.request';
/**
 * The D5b zero-terminal pairing-intent route. The browser receives an opaque
 * one-time intent and a `tex7://pair?…` link; it never receives a credential
 * and never supplies owner/device/world authority.
 */
const PAIRING_INTENT_PATH = '/api/device.pairing.intent';
/**
 * The EXISTING owner-gated device revoke route (mounted by the device host).
 * The browser sends only the logical device id; the server authorizes by exact
 * owner, revokes the enrollment credential, and closes the live socket. No
 * credential or authority material is involved.
 */
const PAIRING_REVOKE_PATH = '/api/device.pairing.revoke';
/** A local-workspace wire refusal, preserving the backend code. */
export class LocalWorkspaceError extends Error {
    code;
    state;
    constructor(code, message, state) {
        super(`local workspace failed: ${code}: ${message}`);
        this.code = code;
        this.state = state;
        this.name = 'LocalWorkspaceError';
    }
}
/** The browser-side D4 local workspace service. Holds no filesystem/device authority. */
export class LocalWorkspaceRuntime {
    api;
    sessions;
    /** UI-facing immutable projection of devices + logical workspaces. */
    list;
    /**
     * @param ctx - client root context.
     * @param api - shared wire client.
     * @param sessions - cross-domain sessions face; its list snapshot is the
     *   authoritative session→local-workspace projection.
     */
    constructor(ctx, api, sessions) {
        this.api = api;
        this.sessions = sessions;
        this.list = createSnapshotStore({
            devices: [],
            workspaces: [],
            active: undefined,
            state: 'idle',
            ready: false,
            error: null,
        });
        // The session projection is authoritative. Re-derive `list.active` whenever
        // the list/selection changes (refresh, rebuild, session open/create), so a
        // remount or a fresh runtime with an EMPTY browser store resolves the same
        // association from the persisted session header alone.
        this.sessions.list.subscribe(() => { this.syncActive(); });
        this.syncActive();
        ctx.reflect.provide('localWorkspaces', this, undefined);
    }
    async agentInstallation() {
        const response = await globalThis.fetch('/api/device/agent-installation', { credentials: 'same-origin' });
        if (!response.ok)
            throw new LocalWorkspaceError('installer-unavailable', 'Device Agent installer is unavailable');
        const body = await response.json();
        return body.installUrl;
    }
    async listDevices() {
        const { result } = await this.api.localWorkspace.listDevices({});
        if (!result.ok)
            throw new LocalWorkspaceError(result.error.code, result.error.message);
        return result.value.devices;
    }
    async requestPairing() {
        // Same-origin authenticated POST to the existing D1 pairing route. No
        // owner/device/world field is sent: the backend derives the owner from the
        // authenticated session and mints the device id. The response carries the
        // short-lived code and never a device credential.
        let response;
        try {
            response = await globalThis.fetch(PAIRING_REQUEST_PATH, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'content-type': 'application/json' },
                body: '{}',
            });
        }
        catch {
            throw new LocalWorkspaceError('pairing-unreachable', 'the pairing request could not reach the host');
        }
        if (response.status === 401) {
            throw new LocalWorkspaceError('pairing-unauthorized', 'pairing requires an authenticated session');
        }
        if (!response.ok) {
            throw new LocalWorkspaceError('pairing-failed', `pairing request failed (${String(response.status)})`);
        }
        const body = await response.json().catch(() => undefined);
        if (typeof body !== 'object' || body === null) {
            throw new LocalWorkspaceError('pairing-failed', 'the pairing response was malformed');
        }
        const record = body;
        if (typeof record.deviceId !== 'string' || typeof record.code !== 'string'
            || typeof record.expiresAt !== 'number' || !Number.isFinite(record.expiresAt)) {
            throw new LocalWorkspaceError('pairing-failed', 'the pairing response was malformed');
        }
        return { deviceId: record.deviceId, code: record.code, expiresAt: record.expiresAt };
    }
    async requestPairingIntent() {
        // Same-origin authenticated POST to the D5b route. No owner/device/world
        // field is sent; the backend derives the owner from the session, mints the
        // device id, and returns an opaque intent plus an OS handoff link.
        let response;
        try {
            response = await globalThis.fetch(PAIRING_INTENT_PATH, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'content-type': 'application/json' },
                body: '{}',
            });
        }
        catch {
            throw new LocalWorkspaceError('pairing-unreachable', 'the pairing request could not reach the host');
        }
        if (response.status === 401) {
            throw new LocalWorkspaceError('pairing-unauthorized', 'pairing requires an authenticated session');
        }
        if (!response.ok) {
            throw new LocalWorkspaceError('pairing-failed', `pairing request failed (${String(response.status)})`);
        }
        const body = await response.json().catch(() => undefined);
        if (typeof body !== 'object' || body === null) {
            throw new LocalWorkspaceError('pairing-failed', 'the pairing response was malformed');
        }
        const record = body;
        if (typeof record.intent !== 'string' || typeof record.deviceId !== 'string'
            || typeof record.expiresAt !== 'number' || !Number.isFinite(record.expiresAt)
            || typeof record.deepLink !== 'string') {
            throw new LocalWorkspaceError('pairing-failed', 'the pairing response was malformed');
        }
        return {
            intent: record.intent,
            deviceId: record.deviceId,
            expiresAt: record.expiresAt,
            deepLink: record.deepLink,
            ...typeof record.installUrl !== 'string' || record.installUrl.length === 0
                ? {} : { installUrl: record.installUrl },
        };
    }
    async pairingStatus(intent) {
        const response = await globalThis.fetch('/api/device/pairing-status', {
            method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ intent }),
        });
        if (!response.ok)
            throw new LocalWorkspaceError('connection-unavailable', 'Connection status is unavailable. Try again.');
        const raw = await response.json();
        if (typeof raw !== 'object' || raw === null)
            throw new LocalWorkspaceError('connection-unavailable', 'Connection status is unavailable.');
        const value = raw;
        if (!['pending', 'connected', 'expired', 'action-required'].includes(String(value.state))
            || (value.state === 'connected' && typeof value.deviceId !== 'string'))
            throw new LocalWorkspaceError('connection-unavailable', 'Connection status is unavailable.');
        return {
            state: value.state,
            ...typeof value.deviceId === 'string' ? { deviceId: value.deviceId } : {},
            ...value.reason === 'account-mismatch' ? { reason: 'account-mismatch' } : {},
        };
    }
    async listWorkspaces() {
        const { result } = await this.api.localWorkspace.list({});
        if (!result.ok)
            throw new LocalWorkspaceError(result.error.code, result.error.message);
        return result.value.workspaces;
    }
    async state(workspaceId) {
        const active = this.list.getSnapshot().active;
        const { result } = await this.api.localWorkspace.state({
            workspaceId, ...(active?.workspaceId === workspaceId ? { sessionId: active.sessionId } : {}),
        });
        if (!result.ok)
            throw new LocalWorkspaceError(result.error.code, result.error.message);
        return result.value.state;
    }
    async refresh() {
        this.list.set({ ...this.list.getSnapshot(), state: 'loading', error: null });
        try {
            await this.sessions.refresh?.();
            const [devices, workspaces] = await Promise.all([this.listDevices(), this.listWorkspaces()]);
            // Rebuild the wire baseline; `active` is re-derived from the authoritative
            // session projection so the baseline can never clobber the association.
            this.list.set({
                devices,
                workspaces,
                active: this.activationFrom(this.sessions.list.getSnapshot(), undefined, workspaces),
                state: 'idle',
                ready: true,
                error: null,
            });
        }
        catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            this.list.set({ ...this.list.getSnapshot(), state: 'error', ready: true, error: message });
            throw error;
        }
    }
    /** Re-pull the baseline after each connection generation. */
    handleConnected() {
        void this.refresh().catch(() => {
            // The list snapshot already carries the failure; a reconnect will retry.
        });
    }
    async openLocalFolder(deviceId, displayName) {
        const { result } = await this.api.localWorkspace.openLocalFolder({
            deviceId,
            ...displayName === undefined ? {} : { displayName },
        });
        const outcome = this.unwrap(result);
        if (!outcome.cancelled)
            this.remember(outcome.workspace);
        return outcome;
    }
    async createLocalFolder(deviceId, displayName) {
        const { result } = await this.api.localWorkspace.createLocalFolder({
            deviceId,
            ...displayName === undefined ? {} : { displayName },
        });
        const outcome = this.unwrap(result);
        if (!outcome.cancelled)
            this.remember(outcome.workspace);
        return outcome;
    }
    /**
     * TEMPORARY Stage B diagnostics. Emits one classification-only line to the
     * local console and, best-effort, to the Harness/server log through the
     * host `clientDiagnostic` route. Values are reduced to booleans here so no
     * caller can leak an id/path/credential; any wire failure is swallowed.
     * @param event - allowlisted event name.
     * @param values - optional classifications (non-boolean entries dropped).
     */
    reportDiagnostic(event, values) {
        const safe = values === undefined
            ? undefined
            : Object.fromEntries(Object.entries(values).filter(([, value]) => typeof value === 'boolean'));
        const facts = safe === undefined || Object.keys(safe).length === 0 ? '' : ` ${JSON.stringify(safe)}`;
        try {
            console.info(`[tex7:local-activation] ${event}${facts}`);
        }
        catch {
            // A hostile console must never break the flow.
        }
        // Temporary host route; a transport/route failure must never affect the
        // flow, so the rejection is deliberately ignored. The composed client API
        // can legitimately omit this OPTIONAL route (a served bundle that predates
        // it), and a synchronous lookup/type failure must degrade the same way as a
        // transport failure: this diagnostic is non-essential, and throwing here
        // would propagate into the calling slot entry and blank the conversation.
        // A missing method must never regain a render-blocking boundary.
        try {
            const host = this.api.host;
            if (typeof host?.clientDiagnostic === 'function') {
                void host.clientDiagnostic({
                    event,
                    ...safe === undefined ? {} : { values: safe },
                }).catch(() => { });
            }
        }
        catch {
            // A hostile/mismatched API surface must never break the flow.
        }
    }
    /**
     * Upsert one cloud-safe workspace into the projection so the picker's own
     * outcome (title included) is observable before/without a wire baseline.
     * @param workspace - the opened workspace's metadata.
     */
    remember(workspace) {
        const current = this.list.getSnapshot();
        const workspaces = current.workspaces.some(item => item.workspaceId === workspace.workspaceId)
            ? current.workspaces.map(item => item.workspaceId === workspace.workspaceId ? workspace : item)
            : [...current.workspaces, workspace];
        this.list.set({ ...current, workspaces });
        this.syncActive();
    }
    /**
     * Derive the active local association from the AUTHORITATIVE session
     * projection: the current session's canonical durable binding, enriched with
     * the cloud-safe workspace row when one is present.
     *
     * A browser-owned active cell is deliberately NOT consulted. A reset/empty
     * browser store, a navigation remount, or a freshly rebuilt runtime still
     * resolves the association because it lives on the persisted session header.
     * @param list - the current session projection (defaults to the live snapshot).
     * @returns the activation, or undefined when the current session is not a
     *   device-local session.
     */
    activationFrom(list = this.sessions.list.getSnapshot(), preferredSessionId, workspaces = this.list.getSnapshot().workspaces) {
        const { current, byId } = list;
        // A caller that just created/opened a device session passes that exact id:
        // adoption must not depend on the list's `current` pointer having caught up.
        const sessionId = preferredSessionId ?? current;
        if (sessionId === undefined)
            return undefined;
        const binding = byId[sessionId]?.localWorkspaceBinding;
        if (binding === undefined)
            return undefined;
        const workspace = workspaces.find(item => item.workspaceId === binding.workspaceId);
        return {
            workspaceId: binding.workspaceId,
            sessionId,
            ...workspace === undefined ? {} : { workspace },
        };
    }
    /** Republish `list.active` from the authoritative session projection. */
    syncActive(preferredSessionId) {
        const snapshot = this.list.getSnapshot();
        const active = this.activationFrom(this.sessions.list.getSnapshot(), preferredSessionId);
        if (snapshot.active?.workspaceId === active?.workspaceId
            && snapshot.active?.sessionId === active?.sessionId
            && snapshot.active?.workspace === active?.workspace)
            return;
        this.list.set({ ...snapshot, active });
    }
    async open(workspaceId, sessionId) {
        const { result } = await this.api.localWorkspace.open({ workspaceId, sessionId });
        if (!result.ok)
            this.throwRefusal(result.error);
        return result.value.world;
    }
    async revoke(workspaceId) {
        const { result } = await this.api.localWorkspace.revoke({ workspaceId });
        if (!result.ok)
            this.throwRefusal(result.error);
        // Drop the revoked row from the local projection immediately so the picker
        // can no longer offer it. The session header binding is a separate durable
        // fact; re-derive `active` (which then surfaces without a workspace title).
        this.list.set({
            ...this.list.getSnapshot(),
            workspaces: this.list.getSnapshot().workspaces.filter(workspace => workspace.workspaceId !== workspaceId),
        });
        this.syncActive();
    }
    async revokeDevice(deviceId) {
        // Same-origin authenticated POST to the EXISTING owner-gated revoke route.
        // Only the logical device id crosses the wire: the server authorizes by
        // exact owner, revokes the enrollment credential, and closes the live
        // socket. There is no browser-only deletion and no authority in the body.
        let response;
        try {
            response = await globalThis.fetch(PAIRING_REVOKE_PATH, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ deviceId }),
            });
        }
        catch {
            throw new LocalWorkspaceError('revoke-unreachable', 'the remove request could not reach the host');
        }
        if (response.status === 401) {
            throw new LocalWorkspaceError('revoke-unauthorized', 'removing a device requires an authenticated session');
        }
        if (response.status === 404) {
            throw new LocalWorkspaceError('revoke-not-found', 'the device was not found');
        }
        if (!response.ok) {
            throw new LocalWorkspaceError('revoke-failed', `the remove request failed (${String(response.status)})`);
        }
        // Re-project the owner's live devices so the removed one disappears; a
        // refresh failure must not turn a successful server revoke into an error.
        await this.refresh().catch(() => { });
    }
    async openLocalWorkspace(workspaceId) {
        // The trusted backend re-authorizes the owned device binding and mints the
        // session already assigned its `device:<DeviceId>` world — no intermediate
        // server session exists. The browser names only the logical workspace id.
        // The create echo carries the canonical binding back and the sessions
        // domain seeds it into the list projection, which is the ONLY authority for
        // this association; the browser persists no binding of its own.
        const sessionId = await this.sessions.create({ localWorkspaceId: workspaceId });
        this.sessions.open(sessionId);
        // Seed `active` from THIS exact created session's durable binding — never
        // from a possibly-lagging `current` pointer — so the visible conversation
        // resolves the device workspace immediately and deterministically.
        this.syncActive(sessionId);
        return sessionId;
    }
    /** Unwrap a folder outcome, mapping a wire refusal to a typed error. */
    unwrap(result) {
        if (!result.ok)
            this.throwRefusal(result.error);
        return result.value;
    }
    /** Throw a typed refusal; a fail-closed local state rides `state`. */
    throwRefusal(error) {
        const state = error.code === 'local-workspace-unavailable'
            ? error.details.state
            : undefined;
        throw new LocalWorkspaceError(error.code, error.message, state);
    }
}
//# sourceMappingURL=service.js.map